import { Component, EventEmitter, Input, Output } from '@angular/core';
import { take } from 'rxjs/operators';
import { ModalService } from 'src/app/core/services/modal.service';
import { VehicleInspectionService } from '../../services/vehicle-inspection.service';
import { todayIso, ukDate } from '../../models/inspection.models';

/** What the modal is acting on - from the calendar, the list or the vehicle tab. */
export interface InspectionTarget {
  id: number;
  registration: string;
  typeName: string;
  nextDueDate: string;
  lastDoneDate: string | null;
}

/**
 * "Mark done" and "Change due date" for one vehicle inspection. Open it with
 * modalService.open(<modalId>) after setting `target` and `mode`.
 */
@Component({
    selector: 'app-inspection-action-modal',
    templateUrl: './inspection-action-modal.component.html',
    standalone: false
})
export class InspectionActionModalComponent {
  @Input() modalId: string = 'inspectionAction';
  @Input() target: InspectionTarget | null = null;
  @Input() mode: 'complete' | 'due' = 'complete';
  @Output() saved: EventEmitter<void> = new EventEmitter();

  doneDate: string = todayIso();
  dueDate: string = '';
  notes: string = '';
  error: string = '';
  busy: boolean = false;
  readonly today = todayIso();
  readonly ukDate = ukDate;

  constructor(private modalService: ModalService, private service: VehicleInspectionService) { }

  /** Call before opening, so the form starts fresh for this inspection. */
  reset(): void {
    this.doneDate = todayIso();
    this.dueDate = this.target ? this.target.nextDueDate : '';
    this.notes = '';
    this.error = '';
  }

  save(): void {
    if (!this.target) {
      return;
    }
    this.error = '';
    const date = this.mode === 'complete' ? this.doneDate : this.dueDate;
    if (!date) {
      this.error = 'Choose a date.';
      return;
    }
    if (this.mode === 'complete' && date > this.today) {
      this.error = 'The date done cannot be in the future.';
      return;
    }

    this.busy = true;
    const request = this.mode === 'complete'
      ? this.service.complete(this.target.id, this.target.nextDueDate, this.doneDate, this.notes)
      : this.service.updateDue(this.target.id, this.dueDate);
    request.pipe(take(1)).subscribe({
      next: () => {
        this.busy = false;
        this.close();
        this.saved.emit();
      },
      error: (e) => {
        this.busy = false;
        this.error = (e && e.error && e.error.message) || 'Something went wrong. Please try again.';
      },
    });
  }

  close(): void {
    this.modalService.close(this.modalId);
  }
}
